import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Table, Icon, Header } from 'semantic-ui-react';
import { TableCells } from './TableCell';
import { useComponentWillMount } from '../../hooks/useComponentWillMount';
import { getSortDirection } from './utility';
import { providerSettings } from './constants';
import { FlexCard, TableDataLoader } from './../';
import { black } from './../utilities';
import { bodySchemaArrayPropType, messagePropType } from './propTypes';

// ***********************************************
// Row Header
// ***********************************************

export const TableHeaderRow = ({
  schema,
  onSortClick,
  setSortColumn,
  sortDirection,
}) => {
  const [sortIndex, setSortIndex] = useState();

  const handleSort = (index, value) => {
    setSortIndex(index);
    onSortClick(value);
  };

  const handleSortDirection = (doSort, direction, initialSort, index) => {
    if (doSort) return getSortDirection(direction);
    if (initialSort) {
      if (sortIndex === index) {
        return getSortDirection('asc');
      }
    }
    return null;
  };

  useComponentWillMount(() => {
    const initialSortedColumn = schema.filter(({ initialSort }) => initialSort);
    if (initialSortedColumn.length > 1)
      console.warn(
        'The table currently only supports an single initial column sort. Only the first column defined in the schema based on index order will be accepted.',
      );
    if (initialSortedColumn.length > 0) {
      const [sortedColumn] = initialSortedColumn;
      const { initialSort, index, value } = sortedColumn;
      if (initialSort) {
        setSortIndex(index);
        setSortColumn(value);
      }
    }
  });

  return schema.map(({ index, value, text, sort, initialSort }) => {
    const sortThisColumn = sortIndex === index;
    const sortDirectionIfApplicable = handleSortDirection(
      sortThisColumn,
      sortDirection,
      initialSort,
      index,
    );
    const doNotSortThisColumn = !sortThisColumn && !sortDirectionIfApplicable;
    return sort ? (
      <Table.HeaderCell
        data-testid={`table-header-cell-${index}`}
        key={index}
        sorted={sortDirectionIfApplicable}
        onClick={() => handleSort(index, value)}
      >
        {text}
        {doNotSortThisColumn && <Icon name="sort" />}
      </Table.HeaderCell>
    ) : (
      <Table.HeaderCell
        data-testid={`table-header-cell-${index}`}
        key={index}
        style={{ cursor: 'not-allowed' }}
      >
        {text}
      </Table.HeaderCell>
    );
  });
};

TableHeaderRow.propTypes = {
  schema: PropTypes.arrayOf(
    PropTypes.shape({
      index: PropTypes.number.isRequired,
      value: PropTypes.string.isRequired,
      text: PropTypes.string.isRequired,
      sort: PropTypes.bool.isRequired,
      initialSort: PropTypes.bool,
    }),
  ).isRequired,
  onSortClick: PropTypes.func.isRequired,
  setSortColumn: PropTypes.func.isRequired,
  sortDirection: PropTypes.string.isRequired,
};

// ***********************************************
// Body Rows
// ***********************************************

export const TableRows = ({
  data,
  schema,
  isLoading,
  pageSize,
  activePage,
  topic,
  loadData,
  isClearing,
}) => {
  const { rowData, state } = data;
  const noDataAndDoneLoading = rowData.length === 0 && !isLoading;

  if (noDataAndDoneLoading) {
    const {
      messaging: {
        noDataFoundMessage: {
          primaryMessage,
          secondaryMessage,
          tertiaryMessage,
          icon,
        },
      },
    } = providerSettings;

    const columnCount = schema.length;

    return (
      <NoTableDataMessage
        data-testid="nodata-message"
        columnCount={columnCount}
        icon={icon}
        primaryMessage={primaryMessage}
        secondaryMessage={secondaryMessage}
        tertiaryMessage={tertiaryMessage}
        topic={topic}
      />
    );
  }

  if (isLoading)
    return (
      <TableDataLoader
        data-testid="data-loader"
        activePage={activePage}
        isLoading={isLoading}
        pageSize={pageSize}
        topic={topic}
        isClearing={isClearing}
      />
    );

  return (
    <Table.Body>
      {rowData.map((row, index) => {
        return (
          <TableRow
            data-testid={`table-body-row-${index}`}
            index={index}
            key={index}
            data={{ row, state }}
            schema={schema}
            loadData={loadData}
          />
        );
      })}
    </Table.Body>
  );
};

TableRows.propTypes = {
  data: PropTypes.shape({
    rowData: PropTypes.array.isRequired,
    state: PropTypes.object,
  }),
  schema: bodySchemaArrayPropType,
  isLoading: PropTypes.bool.isRequired,
  pageSize: PropTypes.number.isRequired,
  activePage: PropTypes.number.isRequired,
  topic: PropTypes.string.isRequired,
};

const TableRow = ({ data, schema, loadData, index }) => (
  <Table.Row data-testid={`table-row-${index}`}>
    <TableCells
      data={data}
      schema={schema}
      loadData={loadData}
      rowIndex={index}
    />
  </Table.Row>
);

TableRow.propTypes = {
  data: PropTypes.shape({
    row: PropTypes.object.isRequired,
    state: PropTypes.object,
  }).isRequired,
  schema: bodySchemaArrayPropType,
};

// ***********************************************
// No data
// ***********************************************

const NoTableDataMessage = ({
  icon = { name: 'search', color: black },
  primaryMessage = {
    message: 'Oops, it appears we were not able to find any [topic] 🤔',
    color: black,
  },
  secondaryMessage = { message: 'Please try again!', color: black },
  tertiaryMessage = null,
  columnCount,
  topic,
}) => {
  return (
    <Table.Body data-testid="nodata-table-body">
      <Table.Row>
        <Table.Cell colSpan={columnCount}>
          <FlexCard
            direction="column"
            style={{ height: '50vh' }}
            verticalAlignment="center"
            horizontalAlignment="center"
          >
            <Header as="h2" icon style={{ color: primaryMessage.color }}>
              <Icon name={icon.name} style={{ color: icon.color }} />
              {primaryMessage.message.replace(
                '[topic]',
                topic?.toLowerCase() ?? 'data',
              )}
            </Header>
            <Header
              as="h3"
              content={secondaryMessage.message}
              style={{ color: secondaryMessage.color }}
            />
            {tertiaryMessage && (
              <Header
                as="h4"
                content={tertiaryMessage.message}
                style={{ color: tertiaryMessage.color }}
              />
            )}
          </FlexCard>
        </Table.Cell>
      </Table.Row>
    </Table.Body>
  );
};

NoTableDataMessage.propTypes = {
  icon: PropTypes.shape({
    name: PropTypes.string.isRequired,
    color: PropTypes.string.isRequired,
  }),
  primaryMessage: messagePropType.isRequired,
  secondaryMessage: messagePropType.isRequired,
  tertiaryMessage: messagePropType,
  columnCount: PropTypes.number.isRequired,
  topic: PropTypes.string,
};
